import React from "react";
import { Link } from "react-router-dom";
import TileSubPanel from "components/TileSubPanel";
import Alert from "components/Alert";
import TabMessages from "containers/BudgetHome/TabMessages";
import { TAB } from "containers/BudgetHome/PageTabs";
import FertiliserList from "./FertiliserList";
import AdditiveList from "./AdditiveList";
import BlockListCard from "./BlockListCard";
import * as icons from "common/icons";
import * as utils from "common/utils";
import { compose, withActions } from "containers/HOC";
import { deleteFertiliser, submitFertiliser } from "./_actions";
import { modal, confirm } from "containers/App/_actions";
import BlockListGroup from "./BlockListGroup";
import * as cropUtils from "containers/BudgetHome/Crops/_utils";
import * as domain from "common/domain";
import * as fertUtils from "./_utils";
import { updateBudget } from "containers/Budget/_actions";
import ActionLink from "components/ActionLink";
import { factoryFertiliser } from "common/domain";
import ZendeskLink from "components/Help/ZendeskLink";
import ScenarioToolLink from "containers/ScenarioTool/ScenarioToolLink";

const Fertiliser = ({ farmId, farm, budget, refData, online, updateBudget, submitFertiliser, modal, showQuickTips, confirm }) => {
    const budgetCopy = utils.clone(budget);
    fertUtils.updateApplicationModel(budgetCopy);
    const { fertiliser = [] } = budgetCopy;
    const { blocks = [] } = budgetCopy;
    const blockList = utils.getBlockList(blocks);
    const allowFertiliser = blocks.some((b) => ["ProductiveFruit", "ProductivePasture", "FodderCrop", "ProductiveCrop"].includes(b.type));
    const hasDCD = (budgetCopy.nitrateInhibitors || []).length > 0;

    const groupByMonthResown = (blocks) => {
        return blocks.reduce((groups, block) => {
            let blockGroup = groups.find((g) => g.monthResown === block.monthResown);
            if (!blockGroup) {
                blockGroup = {
                    blocks: [],
                    monthResown: block.monthResown,
                    type: block.type,
                    calendar: cropUtils.rotationYear(block),
                };
                groups.push(blockGroup);
            }
            blockGroup.blocks.push(block);
            return groups;
        }, []);
    };

    const groupApplications = () => {
        fertiliser.forEach((fert) => {
            fert.applications = fertUtils.groupApplications(fert.applications);
        });
        updateBudget(budgetCopy);
    };

    const groupApplicationsConfirm = () => {
        confirm(`Merging applies identical kg/ha applications to a set of blocks. Are you sure you want to merge?`, groupApplications, true, "");
    };

    const ungroupApplications = () => {
        fertiliser.forEach((fert) => {
            fert.applications = fertUtils.ungroupApplications(fert.applications);
        });
        updateBudget(budgetCopy);
    };

    const ungroupApplicationsConfirm = () => {
        confirm(`Splitting will create individual applications for each block based on kg/ha applications. Are you sure you want to split?`, ungroupApplications, true, "");
    };

    const localSubmitFertiliser = (farmId, budgetId, values) => {
        const { fertiliser } = values;
        submitFertiliser(farmId, budgetId, fertiliser);
    };

    const addFertilser = (material, isLastPreviousLime = false) => {
        const fertiliser = factoryFertiliser();
        fertiliser.id = "NEW";

        const viewModel = {
            material,
            farm,
            budget,
            fertiliser,
            refData,
            isLastPreviousLime,
            customCompositions: [],
            isNew: true,
        };

        modal("FertiliserProductModal", localSubmitFertiliser, viewModel);
    };

    const fruitBlocks = { type: "ProductiveFruit", blocks: blocks.filter((b) => b.type === "ProductiveFruit"), calendar: domain.farmYear };
    const pastureBlocks = { type: "ProductivePasture", blocks: blocks.filter((b) => b.type === "ProductivePasture"), calendar: domain.farmYear };
    const fodderCropGroups = groupByMonthResown(blocks.filter((b) => b.type === "FodderCrop"));
    const productiveCropGroups = groupByMonthResown(blocks.filter((b) => b.type === "ProductiveCrop"));
    const blockGroups = [];
    const cropBlocks = [...fodderCropGroups, ...productiveCropGroups];
    if (fruitBlocks.blocks.length > 0) blockGroups.push(fruitBlocks);
    if (pastureBlocks.blocks.length > 0) blockGroups.push(pastureBlocks);
    cropBlocks.forEach((g) => blockGroups.push(g));

    return (
        <React.Fragment>
            <TabMessages farm={farm} budget={budgetCopy} updateBudget={updateBudget} tab={TAB.FERTILISER} />
            <div className="ActionsBar ActionsBar--super u-print-none">
                <ul className="ActionsBar-links">
                    <li>
                        <ActionLink className="IconLink--arrow-plus" id="fertiliser-add-product" onClick={() => addFertilser("Product")}>
                            <span>Add product</span>
                        </ActionLink>
                    </li>
                    <li>
                        <ActionLink className="IconLink--arrow-plus" id="fertiliser-add-lime" onClick={() => addFertilser("Lime")}>
                            <span>Add lime</span>
                        </ActionLink>
                    </li>
                    <li>
                        <ActionLink className="IconLink--arrow-plus" id="fertiliser-add-organic" onClick={() => addFertilser("Organic")}>
                            <span>Add organic</span>
                        </ActionLink>
                    </li>
                    <li>
                        <ActionLink className="IconLink--arrow-plus" id="fertiliser-add-soluble" onClick={() => addFertilser("Soluble")}>
                            <span>Add soluble</span>
                        </ActionLink>
                    </li>
                    {!hasDCD && (
                        <li>
                            <Link to={`/app/farm/${farm.id}/analysis/${budget.id}/fertiliser/dcd`} className="IconLink--arrow-plus" id="fertiliser-add-dcd">
                                <span>Add DCD</span>
                            </Link>
                        </li>
                    )}
                </ul>

                <ul className="ActionsBar-links">
                    <li>
                        <Link className="IconLink--arrow-plus" id="fertiliser-add-applications" to={`/app/farm/${farmId}/analysis/${budgetCopy.id}/fertiliser/applications`}>
                            <span>Add applications</span>
                        </Link>
                    </li>
                    <li>
                        <ActionLink className="IconLink--group" id="fertiliser-group" onClick={() => groupApplicationsConfirm()}>
                            <span>Merge Applications</span>
                        </ActionLink>
                    </li>
                    <li>
                        <ActionLink className="IconLink--copy" id="fertiliser-split" onClick={() => ungroupApplicationsConfirm()}>
                            <span>Split Applications</span>
                        </ActionLink>
                    </li>
                    {process.env.REACT_APP_DISABLE_SCENARIO_TOOL !== "true" && (
                        <li>
                            <ScenarioToolLink farm={farm} analysisId={budget.id} />
                        </li>
                    )}
                    <li>
                        <ZendeskLink title="Page help" url="https://support.overseer.org.nz/hc/en-us/articles/900000962046" className="IconLink--new-tab u-font-green" />
                    </li>
                </ul>
            </div>

            <TileSubPanel title="Fertilisers" green stickyHeaderName="budget.fertiliser.fertilisers.collapsed">
                {showQuickTips && <Alert type="help" text="This is a list of all fertilisers used on the farm. Select the fertiliser name to edit the applications of this fertiliser or select the edit icon at the end of the row to edit the details for the fertiliser." />}
                {allowFertiliser && (
                    <div className="FarmTable_wrapper">
                        <div className="FarmTable-supplementary">
                            <FertiliserList online={online} farm={farm} budget={budgetCopy} refData={refData} groupApplications={() => groupApplicationsConfirm()} splitApplications={() => ungroupApplicationsConfirm()} />
                        </div>
                    </div>
                )}
                {!allowFertiliser && (
                    <div className="FarmTable_wrapper">
                        <div className="FarmTable-supplementary">
                            <div className="Tile-body">
                                <div className="Tile-body-message">
                                    <img src={icons.add} alt="Plus" />
                                    <p className="lead">No fruit, crop, or pasture blocks have been created</p>
                                    <p>Click the button below to get started</p>
                                    <Link className="IconLink--arrow-plus Button Button--sm u-mt-md" to={`/app/farm/${farmId}/analysis/${budgetCopy.id}/blocks/new`}>
                                        Create Block
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </TileSubPanel>
            {hasDCD && <AdditiveList farm={farm} budget={budgetCopy} />}

            {showQuickTips && <Alert type="help" text="Each block shows the total fertiliser applied each month. Click on the down arrow left of the block name to view each fertiliser application. Click on the block name to edit or add new applications to the block." />}

            {blockGroups.map((g, i) => (
                <BlockListGroup key={i} online={online} blockGroup={g} farmId={farmId} budget={budgetCopy} refData={refData} />
            ))}
            {blockList.nonProductiveBlocks.length > 0 && (
                <TileSubPanel title="Other blocks" stickyHeaderName="budget.fertiliser.otherBlocks.collapsed">
                    {blockList.nonProductiveBlocks.map((block) => (
                        <BlockListCard key={block.id} online={online} block={block} refData={refData} farmId={farmId} budget={budgetCopy} />
                    ))}
                </TileSubPanel>
            )}
        </React.Fragment>
    );
};

export default compose(withActions({ deleteFertiliser, modal, updateBudget, submitFertiliser, confirm }))(Fertiliser);
